import { ChangeEvent, ReactNode, SyntheticEvent } from "react";
import {
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import AutoCompleteCreatable from "components/input/autoCompleteCreatable";
import Selector from "components/select/selector";
import TextInformation from "components/display/textInformation";
import TextFieldInput from "components/input/textField";
import { ContactType } from "components/shareData/shareDialog";
import { ActivityFormConfig } from "../utils/activityFormConfig";

type ActivityAssignmentAndStatusFieldsProps = {
  config: ActivityFormConfig;
  translate: (key: string) => string;
  responsibleOptions: ContactType[];
  responsible: ContactType[];
  sendToResponsible: boolean;
  expireDate: any;
  requireExpireDate: boolean;
  status: string;
  statusOptions: any[];
  submitError: string | null;
  existingActivityId?: string;
  withLogging: boolean;
  eudrReference: string;
  screeningDisabled: boolean;
  screeningActivation?: ReactNode;
  onResponsibleChange: (event: SyntheticEvent<Element, Event>, value: any) => void;
  onResponsibleFilter: (options: any, params: any) => any;
  onSendToResponsibleToggle: () => void;
  onExpireDateChange: (value: any) => void;
  onStatusChange: (event: SelectChangeEvent<string>) => void;
  onEudrReferenceChange: (event: ChangeEvent<HTMLInputElement>) => void;
  elements?: Array<"responsibility" | "scheduleStatus" | "eudrReference" | "activityId">;
};

const ActivityAssignmentAndStatusFields = ({
  config,
  translate,
  responsibleOptions,
  responsible,
  sendToResponsible,
  expireDate,
  requireExpireDate,
  status,
  statusOptions,
  submitError,
  existingActivityId,
  withLogging,
  eudrReference,
  screeningDisabled,
  screeningActivation,
  onResponsibleChange,
  onResponsibleFilter,
  onSendToResponsibleToggle,
  onExpireDateChange,
  onStatusChange,
  onEudrReferenceChange,
  elements,
}: ActivityAssignmentAndStatusFieldsProps) => (
  <>
    {(!elements || elements.includes("responsibility")) &&
      (config.isVisible("responsible") || config.isVisible("notifyResponsible")) && (
      <Grid item xs={12}>
        <Divider />
      </Grid>
    )}

    {(!elements || elements.includes("responsibility")) && config.isVisible("responsible") && (
      <Grid item sm={6} xs={12}>
        <AutoCompleteCreatable
          title={translate("activity-responsible")}
          label={translate("activity-responsible")}
          showLabel={true}
          showTitle={false}
          groupByField="personTypeLabelText"
          renderOptionField="name"
          renderTagField="name"
          data={responsibleOptions}
          value={responsible}
          changeHandler={onResponsibleChange}
          filterHandler={onResponsibleFilter}
          multiple={true}
          filterSelectedOptions={true}
          disabled={config.isDisabled("responsible")}
        />
      </Grid>
    )}

    {(!elements || elements.includes("responsibility")) && config.isVisible("notifyResponsible") && (
      <Grid item sm={6} xs={12}>
        <FormControlLabel
          value="test"
          control={<Checkbox checked={sendToResponsible} className="size-medium" />}
          label={translate("activity-assign-to-responsible-checkbox-text")}
          labelPlacement="end"
          disabled={config.isDisabled("notifyResponsible") || responsible.length === 0}
          onClick={(event) => {
            event.preventDefault();
            onSendToResponsibleToggle();
          }}
          sx={{ ml: 0 }}
        />
      </Grid>
    )}

    {(!elements || elements.includes("scheduleStatus")) &&
      (config.isVisible("expireDate") || config.isVisible("status")) && (
      <Grid item xs={12}>
        <Divider />
      </Grid>
    )}

    {(!elements || elements.includes("scheduleStatus")) && config.isVisible("expireDate") && (
      <Grid item sm={6} xs={12}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjs.locale("da")}>
          <DatePicker
            label={requireExpireDate ? `${translate("expire-date")} *` : translate("expire-date")}
            value={expireDate}
            onChange={onExpireDateChange}
            disabled={config.isDisabled("expireDate")}
            renderInput={(params) => (
              <TextField
                fullWidth
                color="secondary"
                {...params}
                error={submitError === "activity-expire-date-required" && !expireDate}
                sx={{
                  "& .MuiFormLabel-root": { fontSize: 14.5 },
                  "& label": {
                    marginTop: expireDate !== null ? "0px" : "-5px",
                    "&.Mui-focused": { marginTop: "0px" },
                    color: "secondary.600",
                  },
                }}
              />
            )}
          />
        </LocalizationProvider>
      </Grid>
    )}

    {(!elements || elements.includes("scheduleStatus")) && config.isVisible("status") && (
      <Grid item sm={6} xs={12}>
        <Selector
          title={translate("status")}
          value={status}
          options={statusOptions}
          label={true}
          fullWidth={true}
          changeHandler={onStatusChange}
          disabled={config.isDisabled("status")}
        />
      </Grid>
    )}

    {(!elements || elements.includes("eudrReference")) && withLogging && config.isVisible("eudrReference") && (
      <>
        <Grid item xs={12}>
          <Divider />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h5" sx={{ mb: -1 }}>
            {translate("activity-eudr")}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <TextFieldInput
            title={translate("activity-eudr-reference")}
            value={eudrReference}
            showLabel={true}
            fullWidth={true}
            handleChange={onEudrReferenceChange}
            disabled={screeningDisabled || config.isDisabled("eudrReference")}
          />
        </Grid>
      </>
    )}

    {screeningActivation}

    {(!elements || elements.includes("activityId")) && existingActivityId && existingActivityId !== "new" && (
      <>
        <Grid item xs={12}>
          <Divider />
        </Grid>

        <Grid item xs={12}>
          <TextInformation
            label={translate("activity-id")}
            data={existingActivityId}
            sx={{
              mt: -1,
              color: config.isDisabled("saveButton") ? "GrayText" : "text.primary",
            }}
          />
        </Grid>
      </>
    )}
  </>
);

export default ActivityAssignmentAndStatusFields;
